import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Edit, AlertCircle } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  DataTable,
  TruncatedText,
  type DataTableColumnDef
} from '@bliks-io/ui';
import type { Department } from '@bliks-io/contracts';
import type { ProcessDraftBase, ProcessRowData } from './types';

interface ProcessDraftTableProps<TData extends ProcessRowData> {
  /** Array of process drafts to display */
  drafts: ProcessDraftBase<TData>[];
  /** Optional departments for name resolution */
  departments?: Department[];
  /** Optional edit handler - if provided, shows edit button */
  onEdit?: (draft: ProcessDraftBase<TData>) => void;
  /** Optional delete handler - if provided, shows delete button */
  onDelete?: (id: string) => void;
  /** Whether to show row numbers (useful for Excel imports) */
  showRowNumbers?: boolean;
  /** Custom empty state message */
  emptyMessage?: string;
}

/**
 * Unified ProcessDraftTable component
 * Used by both create-processes page and initial process setup
 * Supports both editable and read-only modes; an ownerEmail column appears
 * when the row shape carries one (initial-setup wizard).
 */
export function ProcessDraftTable<TData extends ProcessRowData>({
  drafts,
  departments,
  onEdit,
  onDelete,
  showRowNumbers = false,
  emptyMessage
}: ProcessDraftTableProps<TData>) {
  const { t } = useTranslation();

  // Check if we have any actions to show
  const hasActions = !!onEdit || !!onDelete;
  const hasOwnerEmail = drafts.some((draft) => draft.data.ownerEmail !== undefined);

  const columns = useMemo<DataTableColumnDef<ProcessDraftBase<TData>>[]>(() => {
    // Helper to resolve department name from abbreviation/name
    const resolveDepartmentName = (deptValue: string): string => {
      if (!departments) return deptValue;

      // Try to find by abbreviation first, then by name
      const dept = departments.find(
        (d) => d.abbreviation === deptValue || d.name === deptValue || d._id === deptValue
      );
      return dept ? dept.name : deptValue;
    };
    // Helper to check if a field has errors and get the error messages
    const getFieldErrors = (draft: ProcessDraftBase<TData>, fieldName: string): string[] => {
      if (!draft.errors) return [];
      return draft.errors
        .filter((error) => error.field === fieldName)
        .map((error) => error.message);
    };

    const cols: DataTableColumnDef<ProcessDraftBase<TData>>[] = [];

    // Status column (always first)
    cols.push({
      id: 'status',
      header: '',
      size: 40,
      cell: ({ row }) => {
        const draft = row.original;

        if (draft.isValid) {
          return null;
        }

        const fieldLabelMap: Record<string, string> = {
          name: t('common.Name'),
          description: t('common.Description'),
          department: t('processes.department'),
          owner: t('processes.owner'),
          ownerEmail: t('initialSetup.processes.ownerEmail'),
          accountableEmail: t('initialSetup.columns.processes.accountable')
        };
        const formattedErrors = draft.errors?.map(
          ({ field, message }) => `${fieldLabelMap[field] || field}: ${message}`
        );

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-center cursor-help">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold text-sm">
                    {t('processes.createProcessesPage.validationErrors')}:
                  </p>
                  <ul className="text-xs space-y-0.5">
                    {formattedErrors?.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
    });

    // Row number column (optional)
    if (showRowNumbers) {
      cols.push({
        id: 'rowNumber',
        header: '#',
        size: 60,
        cell: ({ row }) => <span className="text-muted-foreground">{row.original.rowNumber}</span>
      });
    }

    // Name column
    cols.push({
      id: 'name',
      header: t('common.Name'),
      cell: ({ row }) => {
        const draft = row.original;
        const fieldErrors = getFieldErrors(draft, 'name');
        const hasError = fieldErrors.length > 0;

        if (hasError) {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="font-medium text-destructive cursor-help">
                    {draft.data.name || (
                      <span className="italic text-muted-foreground">{t('common.empty')}</span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <ul className="text-xs space-y-0.5">
                    {fieldErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return <div className="font-medium">{draft.data.name}</div>;
      }
    });

    // Description column
    cols.push({
      id: 'description',
      header: t('common.Description'),
      cell: ({ row }) => {
        const draft = row.original;
        const fieldErrors = getFieldErrors(draft, 'description');
        const hasError = fieldErrors.length > 0;

        if (hasError) {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="max-w-[300px] truncate text-destructive cursor-help">
                    {draft.data.description || (
                      <span className="italic text-muted-foreground">{t('common.empty')}</span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <ul className="text-xs space-y-0.5">
                    {fieldErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return <TruncatedText text={draft.data.description} className="max-w-[300px]" />;
      }
    });

    // Department column
    cols.push({
      id: 'department',
      header: t('processes.department'),
      cell: ({ row }) => {
        const draft = row.original;
        const deptValue = draft.data.department;
        const resolvedName = resolveDepartmentName(deptValue);
        const fieldErrors = getFieldErrors(draft, 'department');
        const hasError = fieldErrors.length > 0;

        if (hasError) {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <code className="text-sm bg-destructive/10 text-destructive border border-destructive/30 px-2 py-1 rounded-sm cursor-help">
                    {resolvedName}
                  </code>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <ul className="text-xs space-y-0.5">
                    {fieldErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return (
          <code className="text-sm bg-info-surface-strong text-info px-2 py-1 rounded-sm">
            {resolvedName}
          </code>
        );
      }
    });

    // Prozessverantwortlicher column (initial-setup wizard rows only)
    if (hasOwnerEmail) {
      cols.push({
        id: 'ownerEmail',
        header: t('initialSetup.processes.ownerEmail'),
        cell: ({ row }) => {
          const draft = row.original;
          const fieldErrors = getFieldErrors(draft, 'ownerEmail');

          if (fieldErrors.length > 0) {
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-destructive cursor-help">
                      {draft.data.ownerEmail || (
                        <span className="italic text-muted-foreground">{t('common.empty')}</span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <ul className="text-xs space-y-0.5">
                      {fieldErrors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }

          return <div className="text-sm">{draft.data.ownerEmail}</div>;
        }
      });
    }

    // Actions column (optional)
    if (hasActions) {
      cols.push({
        id: 'actions',
        header: t('common.Actions'),
        size: 100,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {onEdit && (
              <Button variant="ghost" size="small" onClick={() => onEdit(row.original)}>
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="small" onClick={() => onDelete(row.original.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </div>
        )
      });
    }

    return cols;
  }, [t, onEdit, onDelete, showRowNumbers, hasActions, hasOwnerEmail, departments]);

  if (drafts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 border border-dashed rounded-lg">
        <p className="text-muted-foreground">
          {emptyMessage || t('processes.createProcessesPage.noDrafts')}
        </p>
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={drafts}
      isLoading={false}
      emptyMessage={emptyMessage || t('processes.createProcessesPage.noDrafts')}
    />
  );
}
